import { Button, Card, Form, Input, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';

import Text from 'antd/lib/typography/Text';

import PartnerLogoUpload from '../PartnerLogoUpload';
import '../style.scss';

const { Option } = Select;

const wideFormItemProps = {
  labelCol: { span: 4 },
  wrapperCol: { span: 16 },
  labelAlign: 'left' as const,
};

const inputStyle = { width: '100%' };

const rowOptions = [
  { name: '2', value: 2 },
  { name: '3', value: 3 },
  { name: '4', value: 4 },
  { name: '5', value: 5 },
  { name: '6', value: 6 },
  { name: '7', value: 7 },
  { name: '8', value: 8 },
  { name: '9', value: 9 },
];

const colOptions = [
  { name: '2', value: 2 },
  { name: '3', value: 3 },
  { name: '4', value: 4 },
  { name: '5', value: 5 },
  { name: '6', value: 6 },
];

/**
 * Same UX as cms/src/components/TimelineTable.tsx, bound to
 * Content Management fields `timelineName` + `timelineTable`.
 */
export function TimelineSectionFields() {
  const form = Form.useFormInstance();
  const watchedTable = Form.useWatch('timelineTable', form) as string[][] | undefined;
  const [table, setTable] = useState({ rows: 2, cols: 2 });
  const [tableFormat, setTableFormat] = useState<string[][]>([
    ['', ''],
    ['', ''],
  ]);
  const skipNextResizeSync = useRef(false);
  const hydratedKey = useRef<string>('');

  // Keep local editor in sync when form values are loaded/prefilled.
  useEffect(() => {
    if (!Array.isArray(watchedTable) || !watchedTable.length || !Array.isArray(watchedTable[0])) {
      return;
    }

    const key = JSON.stringify(watchedTable);
    if (hydratedKey.current === key) {
      return;
    }
    hydratedKey.current = key;

    skipNextResizeSync.current = true;
    setTable({
      rows: Math.max(2, watchedTable.length),
      cols: Math.max(2, watchedTable[0].length),
    });
    setTableFormat(watchedTable.map(row => [...row]));
  }, [watchedTable]);

  useEffect(() => {
    if (skipNextResizeSync.current) {
      skipNextResizeSync.current = false;
      return;
    }

    const next: string[][] = [];
    for (let i = 0; i < table.rows; i += 1) {
      const row: string[] = [];
      for (let j = 0; j < table.cols; j += 1) {
        row.push(tableFormat[i]?.[j] || '');
      }
      next.push(row);
    }
    setTableFormat(next);
    form.setFieldsValue({ timelineTable: next });
    hydratedKey.current = JSON.stringify(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const handleCellChange = (rowIndex: number, colIndex: number, value: string) => {
    const next = tableFormat.map(row => [...row]);
    next[rowIndex][colIndex] = value;
    setTableFormat(next);
    form.setFieldsValue({ timelineTable: next });
    hydratedKey.current = JSON.stringify(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Form.Item name="timelineName" label="Section Title" {...wideFormItemProps}>
        <Input placeholder="Application Timelines" style={inputStyle} />
      </Form.Item>

      {/* Registers timelineTable so Form.useWatch / submit can read it. */}
      <Form.Item
        name="timelineTable"
        hidden
        style={{ display: 'none' }}
        {...wideFormItemProps}
        trigger="onChange"
        valuePropName="value"
        getValueFromEvent={value => value}
      >
        <TimelineTableValueBridge />
      </Form.Item>

      <div style={{ padding: '16px' }}>
        <div
          style={{
            gap: 32,
            marginBottom: 16,
            borderBottom: '1px solid #e2e8f0',
            gridTemplateColumns: '1fr 1fr',
            display: 'grid',
            paddingBottom: 16,
          }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', marginBottom: 16, width: '100%' }}
          >
            <Text style={{ fontSize: 16, marginBottom: 8 }}>Enter no of rows:</Text>
            <Select
              className="content-rounded-field"
              placeholder="Select rows"
              value={table.rows}
              dropdownClassName="select-checkbox-dropdown"
              onChange={rows => setTable(prev => ({ ...prev, rows }))}
            >
              {rowOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.name}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
            <Text style={{ fontSize: 16, marginBottom: 8 }}>Enter no of columns:</Text>
            <Select
              className="content-rounded-field"
              placeholder="Select columns"
              value={table.cols}
              dropdownClassName="select-checkbox-dropdown"
              onChange={cols => setTable(prev => ({ ...prev, cols }))}
            >
              {colOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.name}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        <Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>Fill Table:</Text>
        <Card
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: 0,
            background: '#fafbfc',
          }}
        >
          {tableFormat.map((row, rowIndex) => (
            <div
              key={`timeline-row-${rowIndex}`}
              style={{ display: 'flex', flexDirection: 'row', gap: 10, marginBottom: 10 }}
            >
              {row.map((cell, colIndex) => (
                <Input
                  key={`timeline-cell-${rowIndex}-${colIndex}`}
                  className="content-rounded-field"
                  maxLength={100}
                  value={cell}
                  placeholder={rowIndex === 0 ? 'Header' : 'Value'}
                  onChange={event => handleCellChange(rowIndex, colIndex, event.target.value)}
                  style={{ width: '100%' }}
                />
              ))}
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

/** Keeps antd Form registered for array values without stringifying them. */
function TimelineTableValueBridge(_props: {
  value?: string[][];
  onChange?: (next: string[][]) => void;
}) {
  return null;
}

export function PopularServicesSectionFields() {
  return (
    <>
      <Form.Item name={['popularServicesSection', 'title']} label="Title" {...wideFormItemProps}>
        <Input placeholder="Popular OCI & Immigration Services" style={inputStyle} />
      </Form.Item>
      <div style={{ margin: '16px' }}>
        <Form.List name={['popularServicesSection', 'services']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <div
                  key={field.key}
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 12,
                    background: '#fafbfc',
                  }}
                >
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}
                  >
                    <p style={{ fontWeight: 600, margin: 0 }}>Popular Service {index + 1}</p>
                    <Button danger size="small" onClick={() => remove(field.name)}>
                      Remove
                    </Button>
                  </div>
                  <Form.Item
                    {...field}
                    name={[field.name, 'title']}
                    label="Title"
                    {...wideFormItemProps}
                    rules={[{ required: true, message: 'Service title is required' }]}
                  >
                    <Input placeholder="OCI Application" style={inputStyle} />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'description']}
                    label="Description"
                    {...wideFormItemProps}
                    rules={[{ required: true, message: 'Description is required' }]}
                  >
                    <Input.TextArea
                      rows={2}
                      maxLength={120}
                      placeholder="Expert support for OCI Passport"
                      style={inputStyle}
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'image']}
                    label="Image"
                    {...wideFormItemProps}
                    rules={[{ required: true, message: 'Service image is required' }]}
                  >
                    <PartnerLogoUpload />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'slug']}
                    label="Slug"
                    {...wideFormItemProps}
                    rules={[{ required: true, message: 'Slug is required' }]}
                  >
                    <Input placeholder="new-oci" style={inputStyle} />
                  </Form.Item>
                </div>
              ))}
              <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      title: '',
                      description: '',
                      image: '',
                      slug: '',
                    })
                  }
                  style={{ color: '#637f92', fontWeight: 600 }}
                >
                  + Add Popular Service
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </div>
    </>
  );
}

export function RelatedArticlesSectionFields() {
  return (
    <>
      <Form.Item name={['relatedArticlesSection', 'title']} label="Title" {...wideFormItemProps}>
        <Input placeholder="Related articles" style={inputStyle} />
      </Form.Item>
      <Form.Item
        name={['relatedArticlesSection', 'subtitle']}
        label="Subtitle"
        {...wideFormItemProps}
      >
        <Input.TextArea rows={2} placeholder="Optional section description" style={inputStyle} />
      </Form.Item>
      <div style={{ margin: '16px' }}>
        <Form.List name={['relatedArticlesSection', 'articles']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <div
                  key={field.key}
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 12,
                    background: '#fafbfc',
                  }}
                >
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}
                  >
                    <p style={{ fontWeight: 600, margin: 0 }}>Article {index + 1}</p>
                    <Button danger size="small" onClick={() => remove(field.name)}>
                      Remove
                    </Button>
                  </div>
                  <Form.Item
                    {...field}
                    name={[field.name, 'name']}
                    label="Title"
                    {...wideFormItemProps}
                    rules={[{ required: true, message: 'Article title is required' }]}
                  >
                    <Input placeholder="Article Title" style={inputStyle} />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'description']}
                    label="Description"
                    {...wideFormItemProps}
                    rules={[{ required: true, message: 'Description is required' }]}
                  >
                    <Input.TextArea
                      rows={2}
                      maxLength={200}
                      placeholder="Brief description of the article"
                      style={inputStyle}
                    />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'url']}
                    label="Image"
                    {...wideFormItemProps}
                    rules={[{ required: true, message: 'Article image is required' }]}
                  >
                    <PartnerLogoUpload />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'externalLink']}
                    label="External Link"
                    {...wideFormItemProps}
                  >
                    <Input placeholder="https://..." style={inputStyle} />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'readTime']}
                    label="Read Time"
                    {...wideFormItemProps}
                  >
                    <Input placeholder="e.g. 5 min read" style={inputStyle} />
                  </Form.Item>
                </div>
              ))}
              <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      name: '',
                      description: '',
                      url: '',
                      externalLink: '',
                      readTime: '',
                    })
                  }
                  style={{ color: '#637f92', fontWeight: 600 }}
                >
                  + Add Related Article
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </div>
    </>
  );
}
